class LoadMoreProducts extends HTMLElement {
  constructor() {
    super();
    this.currentPage = parseInt(this.dataset.currentPage) || 1;
    this.totalPages = parseInt(this.dataset.totalPages) || 1;
    this.pageSize = parseInt(this.dataset.pageSize) || 16;
    this.totalProducts = parseInt(this.dataset.totalProducts) || 0;
    this.sectionId = this.dataset.sectionId;
    this.collectionHandle = this.dataset.collectionHandle;
    this.baseUrl = this.dataset.baseUrl;
    
    this.loadMoreBtn = this.querySelector('.load-more-btn');
    this.loadingSpinner = this.querySelector('.loading-spinner');
    this.paginationInfo = this.querySelector('.pagination-info__text');
    
    this.init();
  }

  init() {
    if (this.loadMoreBtn) {
      this.loadMoreBtn.addEventListener('click', this.handleLoadMore.bind(this));
    }
    
    this.updatePaginationInfo();
    this.toggleLoadMoreButton();
  }

  async handleLoadMore() {
    if (this.currentPage >= this.totalPages) return;
    
    this.setLoadingState(true);
    
    try {
      const nextPage = this.currentPage + 1;
      const response = await this.fetchNextPage(nextPage);
      
      if (response.ok) {
        const data = await response.json();
        
        // Extract section HTML from Section Rendering API response
        const sectionHtml = data[this.sectionId];
        if (sectionHtml) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionHtml, 'text/html');
          
          const newProducts = doc.querySelectorAll('#product-grid .grid__item');
          this.appendProducts(newProducts);
          
          this.currentPage = nextPage;
          this.updatePaginationInfo();
          this.toggleLoadMoreButton();
          
          // Trigger animations if needed
          if (window.theme && window.theme.initAnimations) {
            window.theme.initAnimations();
          }
          
          // Trigger any product card specific initialization
          this.initializeNewProducts();
        } else {
          console.error('Section not found in response:', data);
        }
      } else {
        console.error('Failed to load more products:', response.statusText);
      }
    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      this.setLoadingState(false);
    }
  }

  async fetchNextPage(page) {
    const url = new URL(window.location.origin + this.baseUrl);
    const searchParams = new URLSearchParams(url.search);
    searchParams.set('page', page);
    
    // Add sections parameter for Section Rendering API
    searchParams.set('sections', this.sectionId);
    
    url.search = searchParams.toString();
    
    return fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
  }

  appendProducts(products) {
    const productGrid = document.querySelector('#product-grid');
    if (!productGrid) return;
    
    products.forEach((product, index) => {
      // Update animation order for scroll animations
      const animationOrder = productGrid.children.length + index + 1;
      product.style.setProperty('--animation-order', animationOrder);
      
      // Clone and append the product
      const clonedProduct = product.cloneNode(true);
      productGrid.appendChild(clonedProduct);
    });
  }

  updatePaginationInfo() {
    if (!this.paginationInfo) return;
    
    const itemsPerPage = this.pageSize;
    const currentOffset = (this.currentPage - 1) * itemsPerPage;
    const viewedProducts = Math.min(currentOffset + itemsPerPage, this.totalProducts);
    
    this.paginationInfo.textContent = `You've viewed ${viewedProducts} products of ${this.totalProducts}`;
  }

  setLoadingState(isLoading) {
    if (this.loadMoreBtn) {
      this.loadMoreBtn.disabled = isLoading;
      this.loadMoreBtn.setAttribute('aria-busy', isLoading);
    }
    
    if (this.loadingSpinner) {
      this.loadingSpinner.classList.toggle('active', isLoading);
    }
    
    if (isLoading) {
      this.loadMoreBtn?.classList.add('loading');
    } else {
      this.loadMoreBtn?.classList.remove('loading');
    }
  }

  toggleLoadMoreButton() {
    if (!this.loadMoreBtn) return;
    
    const shouldHide = this.currentPage >= this.totalPages;
    
    if (shouldHide) {
      this.loadMoreBtn.style.display = 'none';
      this.loadMoreBtn.setAttribute('aria-hidden', 'true');
    } else {
      this.loadMoreBtn.style.display = '';
      this.loadMoreBtn.setAttribute('aria-hidden', 'false');
    }
  }

  initializeNewProducts() {
    // Initialize product card sliders if they exist
    const newProductCards = document.querySelectorAll('#product-grid .grid__item:not([data-initialized])');
    
    newProductCards.forEach(card => {
      card.setAttribute('data-initialized', 'true');
      
      // Initialize product card slider functionality if it exists
      const slider = card.querySelector('.product-card-slider');
      if (slider && window.ProductCardSlider) {
        new window.ProductCardSlider(slider);
      }
      
      // Initialize any quick add functionality
      const quickAddBtn = card.querySelector('.quick-add__btn');
      if (quickAddBtn && window.QuickAdd) {
        new window.QuickAdd(quickAddBtn);
      }
      
      // Initialize any variant selectors
      const variantSelector = card.querySelector('.product-variant-selector');
      if (variantSelector && window.VariantSelects) {
        new window.VariantSelects(variantSelector);
      }
    });
  }

  // Method to update data attributes from outside if needed
  updateData(data) {
    Object.keys(data).forEach(key => {
      this.dataset[key] = data[key];
      this[key] = data[key];
    });
    
    this.updatePaginationInfo();
    this.toggleLoadMoreButton();
  }
}

// Register the custom element
customElements.define('load-more-products', LoadMoreProducts);

// Export for potential external use
window.LoadMoreProducts = LoadMoreProducts;
